/**
 * Mobile number handling for records where the country code is optional.
 *
 * A number without a country code is assumed to be Indian, matching how records are
 * stored (`+91` is prefixed before saving). The same helper backs both form validation
 * and the value written to the record, so what is accepted is always what gets stored.
 */

// Indian mobile numbers are 10 digits and start with 6, 7, 8 or 9.
const INDIAN_MOBILE = /^[6-9]\d{9}$/;

const MIN_INTERNATIONAL_DIGITS = 7;
const MAX_INTERNATIONAL_DIGITS = 15;

/**
 * Returns the number in `+<country code><number>` form, or `null` when it is not valid.
 * Spaces, hyphens, brackets and a leading trunk `0` are tolerated.
 */
export function normalizeMobileNumber(value?: string | null): string | null {
	if (typeof value !== 'string') {
		return null;
	}

	let trimmed = value.trim();
	if (!trimmed) {
		return null;
	}

	let hasCountryCode = trimmed.startsWith('+');
	let digits = trimmed.replace(/\D/g, '');

	if (!digits) {
		return null;
	}

	if (hasCountryCode) {
		if (digits.indexOf('91') === 0) {
			let indianPart = digits.substring(2);
			return INDIAN_MOBILE.test(indianPart) ? '+91' + indianPart : null;
		}

		// Other countries are only checked for a plausible length.
		return digits.length >= MIN_INTERNATIONAL_DIGITS && digits.length <= MAX_INTERNATIONAL_DIGITS ? '+' + digits : null;
	}

	// No country code, so treat it as an Indian number.
	if (digits.length === 12 && digits.indexOf('91') === 0) {
		digits = digits.substring(2);
	} else if (digits.length === 11 && digits.indexOf('0') === 0) {
		digits = digits.substring(1);
	}

	return INDIAN_MOBILE.test(digits) ? '+91' + digits : null;
}

export function isValidMobileNumber(value?: string | null): boolean {
	return normalizeMobileNumber(value) !== null;
}
